import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { createFilterItems, TableFilterItem } from 'src/presentations/components/molecules/table/columnHeader';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

type TableType = 'SHIPMENTS' | 'INVOICES' | 'CHARGES';

interface UseAnalyticsColumnHeaderParams {
  tableType: TableType;
  accessorKey: string;
  columnNameMap: Record<string, string>;
  filterSearchRequest: AnalyticSearchRequestDto;
  initialSelectedValues: string[];
}

interface UseAnalyticsColumnHeaderReturn {
  filterItems: TableFilterItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => Promise<void>;
  enableFilter: boolean;
  hasOpenedFilter: boolean;
  setHasOpenedFilter: (value: boolean) => void;
  searchValue: string;
  handleSearchChange: (term: string) => void;
  validSelectedValues: string[];
}

// Map of API column names to their filter path in the request DTO
// This maps the column name (used in API endpoint) to where its filter values are stored
const getColumnFilterMapping = (): Record<string, { location: 'nested' | 'topLevel'; group?: string; key: string }> => ({
  // Shipment filters (nested in filters.shipmentFilters)
  MBL: { location: 'nested', group: 'shipmentFilters', key: 'mbl' },
  SERVICE_TYPE: { location: 'nested', group: 'shipmentFilters', key: 'serviceType' },
  SERVICE: { location: 'nested', group: 'shipmentFilters', key: 'serviceType' }, // Alias used in charges table
  SERVICE_PROVIDER: { location: 'nested', group: 'shipmentFilters', key: 'serviceProviderCode' },
  SERVICE_PROVIDER_NAME: { location: 'nested', group: 'shipmentFilters', key: 'serviceProviderName' },
  MODALITY: { location: 'nested', group: 'shipmentFilters', key: 'modality' },
  VEHICLE_ID: { location: 'nested', group: 'shipmentFilters', key: 'vehicleID' },
  SHIPMENT_NUMBER: { location: 'nested', group: 'shipmentFilters', key: 'shipmentNumberSet' },
  POL: { location: 'nested', group: 'shipmentFilters', key: 'polSet' },
  POD: { location: 'nested', group: 'shipmentFilters', key: 'podSet' },
  SHIPPER_NAME: { location: 'nested', group: 'shipmentFilters', key: 'shipperCodeSet' },
  SHIPPER_COUNTRY: { location: 'nested', group: 'shipmentFilters', key: 'shipperCountrySet' },
  CONSIGNEE_NAME: { location: 'nested', group: 'shipmentFilters', key: 'consigneeCodeSet' },
  CONSIGNEE_COUNTRY: { location: 'nested', group: 'shipmentFilters', key: 'consigneeCountrySet' },
  GOOD_NAME: { location: 'nested', group: 'shipmentFilters', key: 'goodsNameSet' },
  SHIPMENT_CURRENT_DEVIATION_WORKFLOW: { location: 'nested', group: 'shipmentFilters', key: 'shipmentCurrentDeviationWorkflowSet' },
  SHIPMENT_STATUS: { location: 'nested', group: 'shipmentFilters', key: 'shipmentStatusSet' },
  SHIPMENT_STATUS_REASON: { location: 'nested', group: 'shipmentFilters', key: 'shipmentStatusReasonSet' },
  // Charge filters (nested in filters.chargeFilters)
  LEG_TYPE: { location: 'nested', group: 'chargeFilters', key: 'legTypeSet' },
  CHARGE_DOMAIN: { location: 'nested', group: 'chargeFilters', key: 'chargeDomainSet' },
  CHARGE_NAME: { location: 'nested', group: 'chargeFilters', key: 'chargeNameSet' },
  CHARGE_STATUS: { location: 'nested', group: 'chargeFilters', key: 'chargeStatusSet' },
  CHARGE_STATUS_REASON: { location: 'nested', group: 'chargeFilters', key: 'chargeStatusReasonSet' },
  QUANTITY_UNIT: { location: 'nested', group: 'chargeFilters', key: 'quantityUnitSet' },
  // Invoice filters (top-level on AnalyticSearchRequestDto)
  INVOICE_NUMBER: { location: 'topLevel', key: 'invoiceNumberSet' },
  FAP_REF: { location: 'topLevel', key: 'fapRefSet' },
  BILLED_ENTITY: { location: 'topLevel', key: 'billedEntityCodeSet' },
  BILLED_ENTITY_NAME: { location: 'topLevel', key: 'billedEntityCodeSet' }, // Alias
  INVOICE_STATUS: { location: 'topLevel', key: 'invoiceStatusSet' },
  INVOICE_REASON: { location: 'topLevel', key: 'invoiceReasonSet' },
  ACCOUNT_NUMBER: { location: 'topLevel', key: 'accountNumberSet' },
  CHARGE_COUNT: { location: 'topLevel', key: 'chargeCountSet' },
  INVOICE_CHARGES_COUNT: { location: 'topLevel', key: 'chargeCountSet' }, // Alias
});

/**
 * Reusable hook for analytics column header logic
 * Eliminates code duplication across Shipment, Invoice, and Charge column headers
 */
export const useAnalyticsColumnHeader = ({
  tableType,
  accessorKey,
  columnNameMap,
  filterSearchRequest,
  initialSelectedValues,
}: UseAnalyticsColumnHeaderParams): UseAnalyticsColumnHeaderReturn => {
  const [hasOpenedFilter, setHasOpenedFilter] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [initialTotalCount, setInitialTotalCount] = useState<number | null>(null);
  const hasEverOpenedRef = useRef(false);
  const prevInitialSelectedValuesRef = useRef<string>(JSON.stringify(initialSelectedValues));

  // Get API column name from mapping
  const columnName = columnNameMap[accessorKey];
  const enableFilter = Boolean(columnName);

  const pageSize = 25;
  const shouldUseServerSearch = initialTotalCount === null ? true : initialTotalCount > pageSize;
  const serverSearchValue = shouldUseServerSearch ? searchValue : '';

  // Track when dropdown has been opened at least once
  useEffect(() => {
    if (hasOpenedFilter) {
      hasEverOpenedRef.current = true;
    }
  }, [hasOpenedFilter]);

  // Reset state when filters change
  useEffect(() => {
    setInitialTotalCount(null);
  }, [tableType, columnName, filterSearchRequest]);

  // When initialSelectedValues changes and dropdown was previously opened,
  // trigger a refetch so badge updates immediately
  useEffect(() => {
    const currentSelectedStr = JSON.stringify(initialSelectedValues);
    const hasChanged = prevInitialSelectedValuesRef.current !== currentSelectedStr;

    if (hasChanged && hasEverOpenedRef.current) {
      // Trigger refetch by setting hasOpenedFilter to true
      setHasOpenedFilter(true);
    }

    prevInitialSelectedValuesRef.current = currentSelectedStr;
  }, [initialSelectedValues]);

  // Create modified search request that excludes the current column's filter
  // This is the key logic: when opening a column filter, we want to see ALL available options
  // for that column based on OTHER filters, not filtered by its own current selection
  const modifiedSearchRequest = useMemo(() => {
    if (!filterSearchRequest || !columnName) return filterSearchRequest;

    // Deep clone to avoid mutating original
    const cloned = JSON.parse(JSON.stringify(filterSearchRequest)) as AnalyticSearchRequestDto;

    // Get the filter mapping for this column
    const filterMapping = getColumnFilterMapping();
    const mapping = filterMapping[columnName];

    if (mapping) {
      if (mapping.location === 'nested' && mapping.group) {
        // Remove from nested filters (e.g., filters.shipmentFilters.mbl)
        if (cloned.filters?.[mapping.group]?.[mapping.key] !== undefined) {
          delete cloned.filters[mapping.group][mapping.key];
        }
      } else if (mapping.location === 'topLevel') {
        // Remove from top-level (e.g., invoiceNumberSet)
        if (cloned[mapping.key] !== undefined) {
          delete cloned[mapping.key];
        }
      }
    }

    return cloned;
  }, [filterSearchRequest, columnName]);

  // Use infinite query for paginated filter metadata
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    // Use modifiedSearchRequest in queryKey so cache is unique per filter state
    queryKey: cacheKeysUtil.getAnalyticsFilterColumnData(tableType, columnName || '', modifiedSearchRequest, serverSearchValue, { pageNumber: 1, pageSize }),
    queryFn: async ({ pageParam = 0 }) => {
      // Use modifiedSearchRequest in API call - excludes current column's filter
      // API expects 1-based pagination, so add 1 to pageParam
      const response = await tFinancialService.api.getFilterColumnData(tableType, columnName || '', {
        searchRequest: modifiedSearchRequest,
        searchValue: serverSearchValue || null,
        page: { pageNumber: pageParam + 1, pageSize },
      });
      return response.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.values) return undefined;
      if (!shouldUseServerSearch) return undefined;

      const loadedCount = allPages.reduce((sum, page) => {
        return sum + Object.keys(page.values || {}).length;
      }, 0);

      // Check if we have more data to load
      if (lastPage.totalCount && loadedCount >= lastPage.totalCount) {
        return undefined;
      }

      // If last batch was full size, assume there's more
      const lastBatchSize = Object.keys(lastPage.values).length;
      if (lastBatchSize < pageSize) {
        return undefined;
      }

      return allPages.length; // Next page number
    },
    enabled: hasOpenedFilter && enableFilter && !!filterSearchRequest?.currencyReference && !!columnName,
    refetchOnWindowFocus: false,
    staleTime: 0.5 * 60 * 1000,
  });

  useEffect(() => {
    if (searchValue) return;
    if (initialTotalCount !== null) return;

    const totalCount = data?.pages?.[0]?.totalCount;
    if (typeof totalCount === 'number') {
      setInitialTotalCount(totalCount);
    }
  }, [data?.pages, initialTotalCount, searchValue]);

  // Flatten all pages and transform to TableFilterItem[]
  const filterItems = useMemo<TableFilterItem[]>(() => {
    if (!data?.pages) return [];

    // Merge all accumulated values from all pages
    const accumulatedValues: Record<string, number> = {};

    data.pages.forEach((page) => {
      if (page?.values) {
        Object.assign(accumulatedValues, page.values);
      }
    });

    // Do NOT add initialSelectedValues back to the list
    // The API response is the single source of truth for available filter options
    // If a previously selected value is not in the API response, it means it's been
    // filtered out by other active filters (cross-filtering behavior)

    let items = createFilterItems(accumulatedValues, accessorKey);

    if (!shouldUseServerSearch && searchValue.trim()) {
      const lowerSearchValue = searchValue.trim().toLowerCase();
      items = items.filter((item) => item.label.toLowerCase().includes(lowerSearchValue));
    }

    return items;
  }, [accessorKey, data?.pages, searchValue, shouldUseServerSearch]);

  // Handle search change
  const handleSearchChange = useCallback((term: string) => {
    setSearchValue(term);
  }, []);

  // Wrap fetchNextPage to handle promise without returning it
  const handleFetchNextPage = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);

  // Filter initialSelectedValues to only include values that exist in filterItems
  // This ensures the filter dropdown shows correct checked state after cross-filtering
  const validSelectedValues = useMemo(() => {
    if (filterItems.length === 0) {
      // If filterItems is empty (not loaded yet), return initialSelectedValues as-is
      // This allows the badge to show the correct count before dropdown is opened
      return initialSelectedValues;
    }

    // Only keep selected values that exist in the current filterItems
    const availableIds = new Set(filterItems.map((item) => item.id));
    return initialSelectedValues.filter((value) => availableIds.has(value));
  }, [filterItems, initialSelectedValues]);

  return {
    filterItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: handleFetchNextPage,
    enableFilter,
    hasOpenedFilter,
    setHasOpenedFilter,
    searchValue,
    handleSearchChange,
    validSelectedValues, // Return filtered selected values instead of raw initialSelectedValues
  };
};
